import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ConsolidatedDashboard } from '@pnl/shared';
import { useChartColors } from '../../lib/chartColors';
import { formatBrl, formatDateShort } from '../../lib/format';
import { Card } from './Card';

interface Props {
  data: ConsolidatedDashboard;
}

const phaseLabel: Record<ConsolidatedDashboard['perAccount'][number]['phase'], string> = {
  TEST: 'Em teste',
  FUNDED: 'Funded',
  LIVE: 'Live',
};

const statusTone: Record<string, string> = {
  TEST: 'bg-brand-500/15 text-brand-300',
  FUNDED: 'bg-success-500/15 text-success-300',
  LIVE: 'bg-success-500/15 text-success-300',
  BLOWN: 'bg-danger-500/15 text-danger-300',
  RESTARTED: 'bg-warning-500/15 text-warning-300',
  CLOSED: 'bg-ink-700 text-ink-300',
};

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'pos' | 'neg' }) {
  const color =
    tone === 'pos' ? 'text-success-300' : tone === 'neg' ? 'text-danger-300' : 'text-ink-100';
  return (
    <div className="border-ink-800 bg-ink-900 rounded-xl border px-5 py-4">
      <p className="text-2xs text-ink-400 font-semibold uppercase tracking-widest">{label}</p>
      <p className={'mt-1.5 text-xl font-bold tabular-nums ' + color}>{value}</p>
    </div>
  );
}

interface ChartRow {
  label: string;
  balance: number;
}

interface TooltipPayload {
  payload: ChartRow;
}

function CustomTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayload[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]!.payload;
  return (
    <div className="border-ink-700 bg-ink-900 rounded-md border px-3 py-2 text-xs shadow-lg">
      <p className="text-ink-300 font-semibold">{row.label}</p>
      <p className="text-success-300 mt-1 tabular-nums">Saldo: {formatBrl(row.balance)}</p>
    </div>
  );
}

/**
 * Visão consolidada de todas as contas do trader: KPIs somados + curva de
 * equity agregada + tabela por conta. Usada quando o seletor escolhe "Consolidado".
 */
export function ConsolidatedView({ data }: Props) {
  const chart = useChartColors();
  const profitTone =
    data.totalProfitCents > 0 ? 'pos' : data.totalProfitCents < 0 ? 'neg' : undefined;
  const todayTone = data.todayPnlCents > 0 ? 'pos' : data.todayPnlCents < 0 ? 'neg' : undefined;

  const rows: ChartRow[] = useMemo(
    () =>
      data.equityCurve.map((p) => ({
        label: formatDateShort(p.date),
        balance: p.balanceCents,
      })),
    [data.equityCurve],
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Kpi label="Saldo total" value={formatBrl(data.totalBalanceCents)} />
        <Kpi label="Capital total" value={formatBrl(data.totalCapitalCents)} />
        <Kpi
          label="Lucro total"
          value={formatBrl(data.totalProfitCents, { signed: true })}
          tone={profitTone}
        />
        <Kpi
          label="PnL de hoje"
          value={formatBrl(data.todayPnlCents, { signed: true })}
          tone={todayTone}
        />
        <Kpi label="Contas ativas" value={String(data.accountsCount)} />
        <Kpi label="Dias operados" value={String(data.tradingDays)} />
      </div>

      <Card title="Curva de equity consolidada">
        {rows.length === 0 ? (
          <p className="text-ink-400 px-5 py-10 text-center text-sm">
            Sem movimentações ainda — a curva aparece quando houver resultados importados.
          </p>
        ) : (
          <div className="h-[260px] px-2 py-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="consolidated-gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00C9B1" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#00C9B1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke={chart.axis}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke={chart.axis}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) =>
                    `R$ ${(v / 100).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`
                  }
                  domain={['dataMin - 100000', 'dataMax + 100000']}
                  width={80}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: '#1E6FFF', strokeDasharray: '3 3' }}
                />
                <Area
                  type="monotone"
                  dataKey="balance"
                  stroke="#00C9B1"
                  strokeWidth={2}
                  fill="url(#consolidated-gradient)"
                  dot={false}
                  activeDot={{ r: 4, fill: '#1E6FFF', stroke: '#ffffff', strokeWidth: 1.5 }}
                  animationDuration={500}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card title="Por conta">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-ink-800 text-2xs text-ink-400 border-b text-left uppercase tracking-wider">
                <th className="px-5 py-2.5 font-semibold">Conta</th>
                <th className="px-5 py-2.5 font-semibold">Fase</th>
                <th className="px-5 py-2.5 text-right font-semibold">Saldo</th>
                <th className="px-5 py-2.5 text-right font-semibold">Lucro</th>
              </tr>
            </thead>
            <tbody className="divide-ink-800 divide-y">
              {data.perAccount.map((a) => {
                const tone =
                  a.profitCents > 0
                    ? 'text-success-300'
                    : a.profitCents < 0
                      ? 'text-danger-300'
                      : 'text-ink-200';
                return (
                  <tr key={a.id} className="hover:bg-white/5">
                    <td className="text-ink-100 px-5 py-3 font-medium">{a.label}</td>
                    <td className="px-5 py-3">
                      <span
                        className={
                          'text-2xs rounded px-2 py-0.5 font-semibold ' +
                          (statusTone[a.status] ?? 'bg-ink-700 text-ink-300')
                        }
                      >
                        {a.status === 'BLOWN' ? 'Encerrada' : phaseLabel[a.phase]}
                      </span>
                    </td>
                    <td className="text-ink-100 px-5 py-3 text-right tabular-nums">
                      {formatBrl(a.balanceCents)}
                    </td>
                    <td className={'px-5 py-3 text-right tabular-nums ' + tone}>
                      {formatBrl(a.profitCents, { signed: true })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
